import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, ArrowUp } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// Lote 108 (Robson, 24/10/2026): no celular o "X" do topo ficava fora do alcance do dedo. Agora ele é
// maior e mais baixo e, ao rolar a janela, aparecem DOIS botões fixos na lateral (zona do polegar):
// fechar (X) e voltar ao topo (seta). Só no celular (md:hidden); no computador nada muda.
export function DialogScrollAids() {
  const [alvo, setAlvo] = React.useState(null);
  React.useEffect(() => {
    const onScroll = (e) => {
      const t = e.target;
      if (!t || t === document || typeof t.scrollTop !== "number" || !t.closest || !t.closest('[role="dialog"]')) return;
      setAlvo((cur) => (t.scrollTop > 120 ? t : cur === t ? null : cur));
    };
    document.addEventListener("scroll", onScroll, true);
    return () => document.removeEventListener("scroll", onScroll, true);
  }, []);
  if (!alvo) return null;
  return (
    <div className="md:hidden fixed z-[60] right-3 bottom-28 flex flex-col gap-2 pointer-events-auto" data-testid="dialog-scroll-aids" data-dialog-aids="1">
      <button type="button" aria-label="Voltar ao topo" data-testid="dialog-aid-topo"
        onClick={() => alvo.scrollTo({ top: 0, behavior: "smooth" })}
        className="h-12 w-12 rounded-full bg-white text-stone-800 border border-stone-300 shadow-lg inline-flex items-center justify-center">
        <ArrowUp className="h-5 w-5" />
      </button>
      <DialogPrimitive.Close aria-label="Fechar" data-testid="dialog-aid-fechar"
        className="h-12 w-12 rounded-full bg-stone-900 text-white shadow-lg inline-flex items-center justify-center">
        <X className="h-5 w-5" />
      </DialogPrimitive.Close>
    </div>
  );
}

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogScrollAids />
    <DialogPrimitive.Content
      ref={ref}
      onInteractOutside={(e) => {
        // Os botões flutuantes (DialogScrollAids) ficam fora do conteúdo — tocar neles não pode fechar a janela.
        if (e.target?.closest?.("[data-dialog-aids]")) e.preventDefault();
        props.onInteractOutside?.(e);
      }}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-3 top-5 sm:right-4 sm:top-4 rounded-full sm:rounded-sm p-2.5 sm:p-0 bg-stone-100 sm:bg-transparent opacity-90 sm:opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-5 w-5 sm:h-4 sm:w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left pr-10 sm:pr-0", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
